import { useParams } from "react-router-dom";

// Pili brand colors
const PILI_BLUE = "#1C3D55";
const PILI_LIME = "#C9DD3B";
const PILI_DARK = "#0b0c0f";
const PILI_MUTED = "#5A5A5A";
const PILI_PLACEHOLDER = "#9aa1ab";
const PILI_LIGHT_BORDER = "#E6ECF2";

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    backgroundColor: PILI_BLUE,
    fontFamily: "'Oswald', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  card: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: "24px",
    padding: "48px 32px",
    maxWidth: "400px",
    width: "100%",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
  },
  logo: {
    width: "88px",
    height: "88px",
    borderRadius: "20px",
    marginBottom: "24px",
    objectFit: "contain" as const,
  },
  title: {
    fontSize: "32px",
    fontWeight: "700",
    color: PILI_BLUE,
    marginBottom: "12px",
    textAlign: "center" as const,
    letterSpacing: "0.5px",
  },
  subtitle: {
    fontSize: "16px",
    color: PILI_MUTED,
    marginBottom: "32px",
    textAlign: "center" as const,
    maxWidth: "320px",
  },
  openAppButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: PILI_LIME,
    color: PILI_DARK,
    padding: "16px 40px",
    borderRadius: "12px",
    textDecoration: "none",
    fontSize: "18px",
    fontWeight: "600",
    minWidth: "220px",
    marginBottom: "24px",
    cursor: "pointer",
    transition: "transform 0.2s ease",
    letterSpacing: "0.5px",
  },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "24px",
    width: "100%",
    maxWidth: "200px",
  },
  dividerLine: {
    flex: 1,
    height: "1px",
    backgroundColor: PILI_LIGHT_BORDER,
  },
  dividerText: {
    fontSize: "12px",
    color: PILI_PLACEHOLDER,
    textTransform: "uppercase" as const,
  },
  badgesContainer: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "12px",
    marginBottom: "32px",
  },
  appStoreBadge: {
    height: "44px",
    cursor: "pointer",
  },
  instructions: {
    fontSize: "14px",
    color: PILI_PLACEHOLDER,
    textAlign: "center" as const,
    maxWidth: "280px",
    lineHeight: "1.5",
  },
};

/**
 * InvitePage - Web fallback for invite deep links
 *
 * When a user clicks an invite link (https://paypili.com/invite/ABC123) and
 * doesn't have the app installed, they land here. This page shows:
 * - App store download badges
 * - Instructions to install and re-open the link
 */
export default function InvitePage() {
  const { code } = useParams<{ code: string }>();

  // Deep link to open the app directly (works if app is installed)
  const appDeepLink = code ? `pili://invite/${code}` : "pili://";

  const appStoreUrl = "https://apps.apple.com/app/pili/id6754869840";

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <img src="/app-icon.png" alt="Pili" style={styles.logo} />

        <h1 style={styles.title}>You're invited to Pili!</h1>
        <p style={styles.subtitle}>Accept your invite to get started.</p>

        {code && (
          <>
            <a href={appDeepLink} style={styles.openAppButton}>
              Open in Pili
            </a>

            <div style={styles.divider}>
              <div style={styles.dividerLine} />
              <span style={styles.dividerText}>or</span>
              <div style={styles.dividerLine} />
            </div>
          </>
        )}

        <div style={styles.badgesContainer}>
          <a href={appStoreUrl} target="_blank" rel="noopener noreferrer">
            <img
              src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
              alt="Download on the App Store"
              style={styles.appStoreBadge}
            />
          </a>
        </div>

        <p style={styles.instructions}>
          Don't have the app? Download it above, then tap your invite link again.
        </p>
      </div>
    </div>
  );
}
